import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Instagram, Facebook, MessageCircle as Whatsapp, Mail } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const PLATFORMS = {
  instagram: { label: "Instagram", icon: Instagram, color: "#E1306C" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877F2" },
  whatsapp: { label: "WhatsApp", icon: Whatsapp, color: "#25D366" },
};

const STATUS_LABELS = {
  nao_lido: { label: "Não lido", color: "#991B1B" },
  lido: { label: "Lido", color: "#737373" },
  respondido: { label: "Respondido", color: "#166534" },
};

export default function Social() {
  const { apiCall } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [responseOpen, setResponseOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [responseText, setResponseText] = useState("");
  // 🔒 iter73: chave de idempotência da tentativa atual (uma por abertura do modal).
  // Mantida em useRef para NÃO ser regenerada em re-renders. Só zera quando o
  // usuário fecha/cancela ou o envio chega em accepted.
  const idempotencyKeyRef = useRef(null);
  // 🔒 iter73: bloqueio síncrono para evitar cliques duplos disparando dois PUTs.
  const replyInFlightRef = useRef(false);
  const [form, setForm] = useState({ platform: "instagram", sender_name: "", sender_contact: "", message: "" });
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", "/social");
      setMessages(res.data);
    } catch {
      toast.error("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiCall("post", "/social", form);
      toast.success("Mensagem registrada");
      setOpen(false);
      setForm({ platform: "instagram", sender_name: "", sender_contact: "", message: "" });
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const handleOpenResponse = (msg) => {
    setSelected(msg);
    setResponseText(msg.response || "");
    // 🔒 iter73: nova tentativa manual → nova chave. Não gera durante render.
    idempotencyKeyRef.current =
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `idem_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    replyInFlightRef.current = false;
    setResponseOpen(true);
  };

  // 🔒 iter73: garantir que ao FECHAR/CANCELAR o modal a chave é descartada
  // para que a próxima abertura gere uma nova.
  const handleCloseResponse = (open) => {
    setResponseOpen(open);
    if (!open) {
      idempotencyKeyRef.current = null;
      replyInFlightRef.current = false;
    }
  };

  const handleSaveResponse = async () => {
    // 🔒 iter73: bloqueio síncrono anti-duplo-clique. Um clique = um PUT.
    if (replyInFlightRef.current) return;
    if (!responseText.trim()) {
      toast.error("Digite uma resposta");
      return;
    }
    // Garantia: se por algum motivo a chave não foi gerada, geramos aqui
    // (não pode ficar sem chave — backend recusa).
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current =
        (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `idem_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    }
    replyInFlightRef.current = true;
    try {
      const isWhatsApp = selected?.platform === "whatsapp" || selected?.channel === "whatsapp";
      // iter72+73: envio real via Z-API. Retry automático = 0 (nunca renviamos
      // pelo cliente). Se o backend for lento, o mesmo idempotency_key protege.
      const resp = await apiCall("put", `/social/${selected.message_id}`, {
        response: responseText,
        idempotency_key: idempotencyKeyRef.current,
      });
      const outcome = resp?.data?.outcome;
      if (!isWhatsApp) {
        toast.success("Resposta registrada");
      } else if (outcome === "accepted") {
        toast.success(`Enviado (id ${resp.data.zapi_message_id?.slice(0, 8)}…)`);
      } else if (outcome === "unknown") {
        toast.warning("Envio sem confirmação da Z-API — mensagem não marcada como respondida");
      } else {
        toast.success("Registrado");
      }
      // 🔒 iter73: sucesso → descarta chave para próxima tentativa
      idempotencyKeyRef.current = null;
      setResponseOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const outcome = detail?.outcome;
      if (outcome === "failed") {
        toast.error(`Envio falhou: ${detail?.detail || detail?.reason || "erro da Z-API"}`);
      } else if (outcome === "skipped") {
        toast.error("Envios reais estão desativados (kill-switch ativo)");
      } else if (outcome === "sending") {
        toast.warning("Envio já em processamento — aguarde");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Erro ao enviar resposta");
      }
      // 🔒 iter73: em erro NÃO regeneramos a chave — retentativa manual
      // do usuário reutilizaria a mesma, blindando duplicidade no backend.
    } finally {
      replyInFlightRef.current = false;
    }
  };

  const handleMarkAsRead = async (msg) => {
    if (msg.status === "nao_lido") {
      try {
        await apiCall("put", `/social/${msg.message_id}`, { status: "lido" });
        load();
      } catch {
        /* silent */
      }
    }
  };

  const filteredMessages = filter === "all" ? messages : messages.filter((m) => m.platform === filter);

  const formatDate = (date) =>
    new Date(date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  return (
    <div className="space-y-8" data-testid="social-page">
      <PageHeader overline="Comunicação" title="Central de Redes Sociais" subtitle="Gerencie mensagens e interações com seus clientes">
        <Button onClick={() => setOpen(true)} data-testid="add-social-button">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Registrar Mensagem
        </Button>
      </PageHeader>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-secondary/50 rounded-md">
          <TabsTrigger value="all" data-testid="filter-all" className="rounded-md">Todas</TabsTrigger>
          <TabsTrigger value="instagram" data-testid="filter-instagram" className="rounded-md">Instagram</TabsTrigger>
          <TabsTrigger value="facebook" data-testid="filter-facebook" className="rounded-md">Facebook</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="filter-whatsapp" className="rounded-md">WhatsApp</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-6">
          {loading ? (
            <Card className="border border-border bg-card p-12 text-center text-muted-foreground shadow-soft rounded-md">Carregando...</Card>
          ) : filteredMessages.length === 0 ? (
            <Card className="border border-border bg-card p-12 text-center text-muted-foreground shadow-soft rounded-md">
              {(filter === "instagram" || filter === "facebook") ? (
                <div className="space-y-2">
                  <div className="text-base">
                    A integração do {filter === "instagram" ? "Instagram" : "Facebook"} ainda não está conectada.
                  </div>
                  <div className="text-sm">
                    Vá em <a href="/integracoes" className="underline text-foreground">Configurações → Integrações</a> para conectar via login oficial da Meta quando o fluxo for liberado. Enquanto isso, nenhuma mensagem real chega por aqui.
                  </div>
                </div>
              ) : (
                <>Nenhuma mensagem registrada nesta categoria.</>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredMessages.map((msg) => {
                const Platform = PLATFORMS[msg.platform];
                const PlatformIcon = Platform?.icon || Mail;
                return (
                  <Card key={msg.message_id} className="border border-border bg-card p-5 shadow-soft rounded-md cursor-pointer hover:border-foreground/40 transition-colors" onClick={() => handleOpenResponse(msg)} data-testid={`message-card-${msg.message_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md bg-secondary flex items-center justify-center">
                          <PlatformIcon className="h-4 w-4" strokeWidth={1.5} style={{ color: Platform?.color }} />
                        </div>
                        <div>
                          <div className="text-sm font-medium">{msg.sender_name}</div>
                          <div className="text-xs text-muted-foreground">{msg.sender_contact}</div>
                        </div>
                      </div>
                      <div className="text-xs uppercase tracking-widest font-medium" style={{ color: STATUS_LABELS[msg.status]?.color }}>
                        {STATUS_LABELS[msg.status]?.label}
                      </div>
                    </div>
                    <p className="text-sm text-foreground mb-2 line-clamp-3">{msg.message}</p>
                    {msg.response && (
                      <div className="mt-3 pl-3 border-l-2 border-accent">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Resposta</div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{msg.response}</p>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-3">{formatDate(msg.created_at)}</div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* New Message Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Registrar Mensagem</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Plataforma</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger data-testid="social-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram" data-testid="social-platform-instagram">Instagram</SelectItem>
                  <SelectItem value="facebook" data-testid="social-platform-facebook">Facebook</SelectItem>
                  <SelectItem value="whatsapp" data-testid="social-platform-whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Nome do Remetente</Label>
              <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} required data-testid="social-sender-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Contato (@user, telefone)</Label>
              <Input value={form.sender_contact} onChange={(e) => setForm({ ...form, sender_contact: e.target.value })} required data-testid="social-sender-contact-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Mensagem</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={3} data-testid="social-message-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="social-save-button">Registrar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Response Dialog */}
      <Dialog open={responseOpen} onOpenChange={(o) => { handleCloseResponse(o); if (o && selected) handleMarkAsRead(selected); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Mensagem de {selected?.sender_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-secondary/50 rounded-md">
              <p className="text-sm">{selected?.message}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Sua Resposta</Label>
              <Textarea value={responseText} onChange={(e) => setResponseText(e.target.value)} rows={4} data-testid="social-response-textarea" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => handleCloseResponse(false)}>Fechar</Button>
              <Button
                onClick={handleSaveResponse}
                disabled={replyInFlightRef.current}
                data-testid="social-response-save-button"
              >
                Salvar Resposta
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
